import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Sparkles,
  X,
  Calendar as CalendarIcon,
  Pencil,
  Trash2,
  Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const NetworkBadge = ({ network, small = false }) => {
  const colors = {
    facebook: "bg-[#1877F2]",
    instagram: "bg-[#E4405F]",
    linkedin: "bg-[#0A66C2]"
  };
  
  if (small) {
    return (
      <span className={`w-2 h-2 rounded-full ${colors[network]}`} title={network} />
    );
  }
  
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white ${colors[network]}`}>
      {network.charAt(0).toUpperCase()}
    </span>
  );
};

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [brandId, setBrandId] = useState(null);
  
  // Edit/View event state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedEvent, setEditedEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newEvent, setNewEvent] = useState({
    title: "",
    theme: "",
    networks: ["facebook", "instagram", "linkedin"]
  });

  useEffect(() => {
    fetchBrand();
  }, []);

  useEffect(() => {
    if (brandId) {
      fetchEvents();
    }
  }, [currentDate, brandId]);

  const fetchBrand = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      if (response.data.length > 0) {
        setBrandId(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching brand:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/calendar/events`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setEvents(response.data);
      const postsResp = await axios.get(`${API}/posts`);
      const monthPosts = (postsResp.data || []).filter(pt => {
        if (!["scheduled", "published"].includes(pt.status)) return false;
        const d = (pt.scheduled_date || pt.published_date || "").split("T")[0];
        return d >= startDate && d <= endDate;
      });
      setScheduledPosts(monthPosts);
    } catch (error) {
      toast.error("Erreur lors du chargement des événements");
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week (0 = Sunday, 1 = Monday, etc.)
    let startDay = firstDay.getDay();
    // Convert to Monday-based week (0 = Monday, 6 = Sunday)
    startDay = startDay === 0 ? 6 : startDay - 1;
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setShowDialog(true);
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.theme || newEvent.networks.length === 0) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    try {
      await axios.post(`${API}/calendar/events`, {
        brand_id: brandId,
        title: newEvent.title,
        theme: newEvent.theme,
        date: selectedDate,
        networks: newEvent.networks
      });
      
      toast.success("Événement créé avec succès");
      setShowDialog(false);
      setNewEvent({ title: "", theme: "", networks: ["facebook", "instagram", "linkedin"] });
      fetchEvents();
    } catch (error) {
      toast.error("Erreur lors de la création de l'événement");
    }
  };

  const handleGenerateContent = async (eventId) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/calendar/events/${eventId}/generate`);
      toast.success(`${response.data.posts?.length || 0} posts générés !`);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`);
      toast.success("Événement supprimé");
      setShowDeleteConfirm(false);
      setShowEventDialog(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setEditedEvent({ ...event });
    setIsEditing(false);
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!editedEvent.title || !editedEvent.theme || editedEvent.networks.length === 0) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/calendar/events/${editedEvent.id}`, {
        brand_id: brandId,
        title: editedEvent.title,
        theme: editedEvent.theme,
        date: editedEvent.date,
        networks: editedEvent.networks
      });
      
      toast.success("Événement modifié avec succès");
      setShowEventDialog(false);
      setSelectedEvent(null);
      setIsEditing(false);
      fetchEvents();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const toggleEditNetwork = (network) => {
    setEditedEvent(prev => ({
      ...prev,
      networks: prev.networks.includes(network)
        ? prev.networks.filter(n => n !== network)
        : [...prev.networks, network]
    }));
  };

  const toggleNetwork = (network) => {
    setNewEvent(prev => ({
      ...prev,
      networks: prev.networks.includes(network)
        ? prev.networks.filter(n => n !== network)
        : [...prev.networks, network]
    }));
  };

  const days = getDaysInMonth();

  return (
    <div className="space-y-6 animate-slide-up" data-testid="calendar-page">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevMonth}
                data-testid="prev-month"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-xl font-bold min-w-[200px] text-center">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button
                variant="outline"
                size="icon"
                onClick={handleNextMonth}
                data-testid="next-month"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              onClick={() => setCurrentDate(new Date())}
              variant="outline"
            >
              Aujourd'hui
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {/* Days header */}
          <div className="grid grid-cols-7 border-b border-border">
            {DAYS.map((day) => (
              <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground border-r border-border last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar cells */}
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              return (
                <div
                  key={index}
                  className={`calendar-day min-h-[120px] border-r border-b border-border last:border-r-0 cursor-pointer transition-colors
                    ${!day ? 'bg-muted/20' : ''}
                    ${isToday(day) ? 'today' : ''}
                  `}
                  onClick={() => handleDayClick(day)}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${isToday(day) ? 'text-primary' : 'text-muted-foreground'}`}>
                        {day}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event) => (
                          <div
                            key={event.id}
                            className={`calendar-event cursor-pointer hover:opacity-80 bg-[#7b1f2b]/30 text-[#d98a94]`}
                            onClick={(e) => handleEventClick(event, e)}
                            title={`${event.title} - Cliquez pour modifier`}
                            data-testid={`event-${event.id}`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="truncate">{event.title}</span>
                              {event.posts_generated ? (
                                <span className="text-xs">✓</span>
                              ) : (
                                <Sparkles className="w-3 h-3 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex gap-0.5 mt-0.5">
                              {event.networks?.map((network) => (
                                <NetworkBadge key={network} network={network} small />
                              ))}
                            </div>
                          </div>
                        ))}
                        {scheduledPosts.filter(pt => {
                          const d = (pt.scheduled_date || pt.published_date || "").split("T")[0];
                          const dayStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                          return d === dayStr;
                        }).map(pt => (
                          <div
                            key={pt.id}
                            className={`calendar-event cursor-pointer hover:opacity-80 ${pt.network === "facebook" ? "bg-blue-500/20 text-blue-400" : pt.network === "instagram" ? "bg-orange-500/20 text-orange-400" : "bg-cyan-500/20 text-cyan-400"}`}
                            onClick={(e) => { e.stopPropagation(); setPreviewPost(pt); }}
                            title="Cliquez pour voir l'aperçu"
                          >
                            <div className="flex items-center gap-1">
                              <span>{pt.status === "published" ? "✅" : pt.status === "failed" ? "❌" : "🕒"}</span>
                              <span className="truncate">
                                {(pt.scheduled_date || "").includes("T")
                                  ? new Date(pt.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) + " "
                                  : ""}
                                {pt.network}
                              </span>
                            </div>
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-muted-foreground">
                            +{dayEvents.length - 3} autres
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#7b1f2b]/30" />
          <span>Notes / idées (✓ = générée)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500/20" />
          <span>Post Facebook</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-orange-500/20" />
          <span>Post Instagram</span>
        </div>
        <div className="flex items-center gap-2">
          <span>🕒 planifié · ✅ publié</span>
        </div>
        <div className="flex items-center gap-2">
          <NetworkBadge network="facebook" small />
          <span>Facebook</span>
        </div>
        <div className="flex items-center gap-2">
          <NetworkBadge network="instagram" small />
          <span>Instagram</span>
        </div>
        <div className="flex items-center gap-2">
          <NetworkBadge network="linkedin" small />
          <span>LinkedIn</span>
        </div>
      </div>

      {/* Post Preview Dialog */}
      <Dialog open={!!previewPost} onOpenChange={(open) => !open && setPreviewPost(null)}>
        <DialogContent data-testid="post-preview-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewPost?.status === "published" ? "✅ Publié" : previewPost?.status === "failed" ? "❌ Échec" : "🕒 Planifié"}
              {previewPost?.network && (
                <span className="capitalize text-sm font-normal text-muted-foreground">— {previewPost.network}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {previewPost?.scheduled_date && (
                <>Programmé le {new Date(previewPost.scheduled_date).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</>
              )}
              {previewPost?.status === "published" && previewPost?.published_date && (
                <> · Publié le {new Date(previewPost.published_date).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {(previewPost?.image_url || previewPost?.image_base64) && (
              <img
                src={previewPost.image_base64 ? `data:image/png;base64,${previewPost.image_base64}` : previewPost.image_url}
                alt="Visuel du post"
                className="w-full rounded-lg border"
              />
            )}
            <p className="text-sm whitespace-pre-wrap">{previewPost?.content}</p>
            {previewPost?.status === "failed" && previewPost?.last_error && (
              <p className="text-sm text-red-400 border border-red-400/30 rounded-md p-2">
                Motif de l'échec : {previewPost.last_error}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="create-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Nouvel événement - {selectedDate}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre</Label>
              <Input
                id="title"
                placeholder="Ex: Lancement produit été"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                data-testid="event-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="theme">Thème / Sujet</Label>
              <Input
                id="theme"
                placeholder="Ex: Nouveaux services pour entrepreneurs"
                value={newEvent.theme}
                onChange={(e) => setNewEvent({ ...newEvent, theme: e.target.value })}
                data-testid="event-theme-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Réseaux sociaux</Label>
              <div className="flex gap-4">
                {["facebook", "instagram", "linkedin"].map((network) => (
                  <div key={network} className="flex items-center gap-2">
                    <Checkbox
                      id={network}
                      checked={newEvent.networks.includes(network)}
                      onCheckedChange={() => toggleNetwork(network)}
                      data-testid={`network-checkbox-${network}`}
                    />
                    <Label htmlFor={network} className="capitalize cursor-pointer">
                      {network}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateEvent} className="btn-primary" data-testid="create-event-submit">
              <Plus className="w-4 h-4 mr-2" />
              Créer l'événement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={(open) => {
        if (!open) {
          setShowEventDialog(false);
          setSelectedEvent(null);
          setIsEditing(false);
        }
      }}>
        <DialogContent data-testid="event-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              {isEditing ? "Modifier l'événement" : "Détails de l'événement"}
            </DialogTitle>
            {selectedEvent && (
              <DialogDescription>
                {selectedEvent.date} {selectedEvent.posts_generated && "• Posts déjà générés"}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {editedEvent && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Titre</Label>
                <Input
                  id="edit-title"
                  value={editedEvent.title}
                  onChange={(e) => setEditedEvent({ ...editedEvent, title: e.target.value })}
                  disabled={!isEditing}
                  data-testid="edit-event-title"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-theme">Thème / Sujet</Label>
                <Input
                  id="edit-theme"
                  value={editedEvent.theme}
                  onChange={(e) => setEditedEvent({ ...editedEvent, theme: e.target.value })}
                  disabled={!isEditing}
                  data-testid="edit-event-theme"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editedEvent.date}
                  onChange={(e) => setEditedEvent({ ...editedEvent, date: e.target.value })}
                  disabled={!isEditing}
                  data-testid="edit-event-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Réseaux sociaux</Label>
                <div className="flex gap-4">
                  {["facebook", "instagram", "linkedin"].map((network) => (
                    <div key={network} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-${network}`}
                        checked={editedEvent.networks?.includes(network)}
                        onCheckedChange={() => toggleEditNetwork(network)}
                        disabled={!isEditing}
                        data-testid={`edit-network-${network}`}
                      />
                      <Label htmlFor={`edit-${network}`} className="capitalize cursor-pointer">
                        {network}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Generate content button */}
              {!selectedEvent?.posts_generated && !isEditing && (
                <div className="pt-2">
                  <Button 
                    onClick={() => handleGenerateContent(selectedEvent.id)}
                    disabled={generating}
                    className="w-full"
                    variant="secondary"
                    data-testid="generate-from-dialog"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {generating ? "Génération en cours..." : "Générer les posts"}
                  </Button>
                </div>
              )}

              {selectedEvent?.posts_generated && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <span>✓</span>
                    Les posts ont déjà été générés pour cet événement
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsEditing(false);
                    setEditedEvent({ ...selectedEvent });
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleSaveEvent} 
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-event-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowDeleteConfirm(true)}
                  data-testid="delete-event-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-event-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'événement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{selectedEvent?.title}" ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteEvent(selectedEvent?.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
